import { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase/firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  writeBatch
} from 'firebase/firestore';

/**
 * Utility for managing aggregated statistics to avoid scanning large collections
 */
export class AggregationsManager {
  constructor(agentId) {
    this.agentId = agentId;
    this.aggregationsRef = doc(db, 'agent-aggregations', agentId);
    this.lastClientUpdate = 0;
    this.lastPolicyUpdate = 0;
    this.minUpdateInterval = 30 * 1000; // Minimum 30 seconds between updates
  }

  /**
   * Get current aggregation data
   */
  async getAggregations() {
    try {
      const docSnap = await getDoc(this.aggregationsRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return this.initializeAggregations();
    } catch (error) {
      console.error('Error getting aggregations:', error);
      return {};
    }
  }

  /**
   * Initialize aggregation document with default values
   */
  async initializeAggregations() {
    const defaultAggregations = {
      totalClients: 0,
      totalPolicies: 0,
      newLeadsCount: 0,
      activePoliciesCount: 0,
      expiredPoliciesCount: 0,
      totalPremium: 0,
      lastUpdated: new Date().toISOString(),
      clientStats: {
        newLeads: 0,
        customers: 0,
        total: 0
      },
      policyStats: {
        active: 0,
        expired: 0,
        total: 0
      }
    };

    try {
      await setDoc(this.aggregationsRef, defaultAggregations);
      return defaultAggregations;
    } catch (error) {
      console.error('Error initializing aggregations:', error);
      return defaultAggregations;
    }
  }

  /**
   * Update aggregations when clients change
   */
  async updateClientAggregations(clientData) {
    try {
      // Debounce: Skip if updated less than 30 seconds ago (prevents write amplification)
      const now = Date.now();
      if (now - this.lastClientUpdate < this.minUpdateInterval) {
        console.log('⏭️ Skipping client aggregation update (debounced)');
        return;
      }
      this.lastClientUpdate = now;

      const isNewLead = clientData.status === 'New Lead';
      const updates = {
        lastUpdated: new Date().toISOString()
      };

      if (clientData._operation === 'add') {
        updates.totalClients = increment(1);
        updates['clientStats.total'] = increment(1);

        if (isNewLead) {
          updates.newLeadsCount = increment(1);
          updates['clientStats.newLeads'] = increment(1);
        } else {
          updates['clientStats.customers'] = increment(1);
        }
      } else if (clientData._operation === 'update' && clientData.previousStatus) {
        // Handle status changes
        if (clientData.previousStatus !== clientData.status) {
          if (clientData.previousStatus === 'New Lead' && clientData.status !== 'New Lead') {
            updates.newLeadsCount = increment(-1);
            updates['clientStats.newLeads'] = increment(-1);
            updates['clientStats.customers'] = increment(1);
          } else if (clientData.previousStatus !== 'New Lead' && clientData.status === 'New Lead') {
            updates.newLeadsCount = increment(1);
            updates['clientStats.newLeads'] = increment(1);
            updates['clientStats.customers'] = increment(-1);
          }
        }
      } else if (clientData._operation === 'delete') {
        updates.totalClients = increment(-1);
        updates['clientStats.total'] = increment(-1);

        if (isNewLead) {
          updates.newLeadsCount = increment(-1);
          updates['clientStats.newLeads'] = increment(-1);
        } else {
          updates['clientStats.customers'] = increment(-1);
        }
      }

      await setDoc(this.aggregationsRef, updates, { merge: true });
    } catch (error) {
      console.error('Error updating client aggregations:', error);
    }
  }

  /**
   * Update aggregations when policies change
   */
  async updatePolicyAggregations(policyData) {
    try {
      // Debounce: Skip if updated less than 30 seconds ago (prevents write amplification)
      const now = Date.now();
      if (now - this.lastPolicyUpdate < this.minUpdateInterval) {
        console.log('⏭️ Skipping policy aggregation update (debounced)');
        return;
      }
      this.lastPolicyUpdate = now;

      const isActive = policyData.status === 'active';
      const isExpired = policyData.expiryDate && new Date(policyData.expiryDate) < new Date();
      const updates = {
        lastUpdated: new Date().toISOString()
      };

      if (policyData._operation === 'add') {
        updates.totalPolicies = increment(1);
        updates['policyStats.total'] = increment(1);
        updates.totalPremium = increment(policyData.premiumAmount || 0);

        if (isActive) {
          updates.activePoliciesCount = increment(1);
          updates['policyStats.active'] = increment(1);
        }

        if (isExpired) {
          updates.expiredPoliciesCount = increment(1);
          updates['policyStats.expired'] = increment(1);
        }
      } else if (policyData._operation === 'update') {
        // Handle status/premium changes
        const premiumChange = (policyData.premiumAmount || 0) - (policyData.previousPremium || 0);
        if (premiumChange !== 0) {
          updates.totalPremium = increment(premiumChange);
        }

        if (policyData.previousStatus !== policyData.status) {
          if (policyData.previousStatus === 'active' && policyData.status !== 'active') {
            updates.activePoliciesCount = increment(-1);
            updates['policyStats.active'] = increment(-1);
          } else if (policyData.previousStatus !== 'active' && policyData.status === 'active') {
            updates.activePoliciesCount = increment(1);
            updates['policyStats.active'] = increment(1);
          }

          // Check expiry status changes
          const wasExpired = policyData.previousExpiryDate && new Date(policyData.previousExpiryDate) < new Date();
          const nowExpired = policyData.expiryDate && new Date(policyData.expiryDate) < new Date();

          if (wasExpired && !nowExpired) {
            updates.expiredPoliciesCount = increment(-1);
            updates['policyStats.expired'] = increment(-1);
          } else if (!wasExpired && nowExpired) {
            updates.expiredPoliciesCount = increment(1);
            updates['policyStats.expired'] = increment(1);
          }
        }
      } else if (policyData._operation === 'delete') {
        updates.totalPolicies = increment(-1);
        updates['policyStats.total'] = increment(-1);
        updates.totalPremium = increment(-(policyData.premiumAmount || 0));

        if (isActive) {
          updates.activePoliciesCount = increment(-1);
          updates['policyStats.active'] = increment(-1);
        }

        if (isExpired) {
          updates.expiredPoliciesCount = increment(-1);
          updates['policyStats.expired'] = increment(-1);
        }
      }

      await setDoc(this.aggregationsRef, updates, { merge: true });
    } catch (error) {
      console.error('Error updating policy aggregations:', error);
    }
  }

  /**
   * Recalculate aggregations from scratch (for maintenance)
   */
  async recalculateAggregations() {
    try {
      console.log('Recalculating aggregations for agent:', this.agentId);

      // Get all clients
      const clientsRef = collection(db, 'agents', this.agentId, 'customers');
      const clientsSnapshot = await getDocs(clientsRef);

      // Get all policies
      const policiesRef = collection(db, 'agents', this.agentId, 'policies');
      const policiesSnapshot = await getDocs(policiesRef);

      // Calculate aggregations
      let totalClients = 0;
      let newLeadsCount = 0;
      let activePoliciesCount = 0;
      let expiredPoliciesCount = 0;
      let totalPremium = 0;

      clientsSnapshot.forEach(doc => {
        totalClients++;
        if (doc.data().status === 'New Lead') {
          newLeadsCount++;
        }
      });

      policiesSnapshot.forEach(doc => {
        const policy = doc.data();
        totalPremium += policy.premiumAmount || 0;

        if (policy.status === 'active') {
          activePoliciesCount++;
        }

        if (policy.expiryDate && new Date(policy.expiryDate) < new Date()) {
          expiredPoliciesCount++;
        }
      });

      const updates = {
        totalClients,
        totalPolicies: policiesSnapshot.size,
        newLeadsCount,
        activePoliciesCount,
        expiredPoliciesCount,
        totalPremium,
        lastUpdated: new Date().toISOString(),
        clientStats: {
          newLeads: newLeadsCount,
          customers: totalClients - newLeadsCount,
          total: totalClients
        },
        policyStats: {
          active: activePoliciesCount,
          expired: expiredPoliciesCount,
          total: policiesSnapshot.size
        }
      };

      await setDoc(this.aggregationsRef, updates);
      console.log('Aggregations recalculated successfully');

    } catch (error) {
      console.error('Error recalculating aggregations:', error);
    }
  }
}

/**
 * Hook for using aggregations in React components
 */
export const useAggregations = (agentId) => {
  const [aggregations, setAggregations] = useState({});
  const [loading, setLoading] = useState(true);
  const managerRef = useRef(null);

  useEffect(() => {
    if (agentId) {
      managerRef.current = new AggregationsManager(agentId);

      const loadAggregations = async () => {
        const data = await managerRef.current.getAggregations();
        setAggregations(data);
        setLoading(false);
      };

      loadAggregations();
    }
  }, [agentId]);

  const refreshAggregations = useCallback(async () => {
    if (managerRef.current) {
      const data = await managerRef.current.getAggregations();
      setAggregations(data);
    }
  }, []);

  const updateClientAggregation = useCallback(async (clientData) => {
    if (managerRef.current) {
      await managerRef.current.updateClientAggregations(clientData);
      await refreshAggregations();
    }
  }, [refreshAggregations]);

  const updatePolicyAggregation = useCallback(async (policyData) => {
    if (managerRef.current) {
      await managerRef.current.updatePolicyAggregations(policyData);
      await refreshAggregations();
    }
  }, [refreshAggregations]);

  const recalculateAll = useCallback(async () => {
    if (managerRef.current) {
      await managerRef.current.recalculateAggregations();
      await refreshAggregations();
    }
  }, [refreshAggregations]);

  return {
    aggregations,
    loading,
    refreshAggregations,
    updateClientAggregation,
    updatePolicyAggregation,
    recalculateAll
  };
};
